import { useGetDevicesRemoteDesktopSupportQuery } from '../api/DeviceConfigurationAPI';
import { COMMANDER_VOLATILE_DATA_POOLING_INTERVAL } from '../../api/Constants';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAllDevices } from '../../hooks/useAllDevices';
import { useAppSelector } from '../../../store.model';
import { getRightUserUtilSelector } from '../../../rights/selectors';

/**
 * Hook to bulk-fetch remote desktop support for all devices with polling.
 *
 * Follows the same pattern as useCommanderStatus for efficient querying.
 * Fetches capabilities for all devices in a single API call instead of one call per device.
 * Results are cached and automatically polled at regular intervals.
 *
 * Use this hook at the page/grid level when rendering multiple devices.
 * For single device detail views, use useDeviceRemoteDesktopSupport() instead.
 *
 * @param deviceId - Optional: To get status for a specific device
 * @returns { data, isLoading, error, deviceRemoteDesktopSupport }
 */
function useDevicesRemoteDesktopSupport(deviceId = '') {
  const { data: devicesStructure, ...restDevices } = useAllDevices();
  const deviceIds = devicesStructure?.result ?? [];
  const rightsUserUtil = useAppSelector(getRightUserUtilSelector);
  const hasRemoteDesktopUsageRight =
    rightsUserUtil.hasRightsToRemoteDesktopUsage();

  return useGetDevicesRemoteDesktopSupportQuery(
    deviceIds.length > 0 && hasRemoteDesktopUsageRight ? deviceIds : skipToken,
    {
      skip: !deviceIds?.length || !hasRemoteDesktopUsageRight,
      pollingInterval: COMMANDER_VOLATILE_DATA_POOLING_INTERVAL,
      selectFromResult: ({ data, ...rest }) => {
        // Optional: select specific device if deviceId provided
        const deviceRemoteDesktopSupport =
          deviceId && data?.entities ? data.entities[deviceId] : undefined;
        return {
          ...rest,
          data,
          deviceRemoteDesktopSupport,
        };
      },
    }
  );
}

export default useDevicesRemoteDesktopSupport;
